// A checkout can change on the server while a patient has it open: staff can
// replace the product on a pending order, and every reload of the recovery link
// issues a new checkout capability that retires the previous one. The cached
// copy in sessionStorage is therefore only a first paint; these helpers adopt the
// server's current checkout over it.

export const CHECKOUT_UPDATED_NOTICE = (
  "Your order was updated. Please review the details below before you authorize."
);
export const CHECKOUT_REFRESHED_NOTICE = (
  "We refreshed your checkout. Please review it and try again."
);

// Codes a payment or re-price request fails with when its capability or session
// revision is no longer current.
const STALE_CHECKOUT_ERROR_CODES = new Set([
  "checkout_capability_unavailable",
  "stale_checkout_capability",
  "invalid_checkout_capability",
  "runtime_revision_stale",
  "stale_revision",
  "runtime_patient_mismatch",
]);

export const isStaleCheckoutError = (error) => (
  [error?.code, error?.rawCode].some(
    (code) => STALE_CHECKOUT_ERROR_CODES.has(String(code || "").toLowerCase()),
  )
);

export const isUsableCheckout = (checkout) => Boolean(
  checkout?.checkout_capability && Array.isArray(checkout.treatments),
);

export const checkoutChanged = (current, recovered) => (
  String(current?.session_revision ?? "") !== String(recovered?.session_revision ?? "")
  || String(current?.quote?.id ?? "") !== String(recovered?.quote?.id ?? "")
);

const newIdempotencyKey = () => (
  globalThis.crypto?.randomUUID?.()
  || `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

// The recovered capability must always replace the cached one, even when
// nothing changed: loading the recovery link retired the cached capability.
// A changed quote is a changed payment intent, so it gets a new replay key,
// exactly as a re-price does; an unchanged one keeps the attempt's key.
export const adoptRecoveredCheckout = (current, recovered, apiBaseUrl) => {
  const changed = checkoutChanged(current, recovered);
  const runtimeApiBaseUrl = (
    recovered.runtime_api_base_url || current?.runtime_api_base_url || apiBaseUrl
  );
  const checkout = {
    ...recovered,
    ...(runtimeApiBaseUrl ? { runtime_api_base_url: runtimeApiBaseUrl } : {}),
    ...(current?.patient_name ? { patient_name: current.patient_name } : {}),
    ...(changed
      ? { client_idempotency_key: newIdempotencyKey() }
      : current?.client_idempotency_key
        ? { client_idempotency_key: current.client_idempotency_key }
        : {}),
  };
  return { checkout, changed };
};
